import { useAgentChat } from '@/contexts/AgentChatContext';
import { classNames } from '@/utils';
import { useEffect, useMemo, useState } from 'react';
import { AgentWorkingEmptyState } from '../Editor/AgentWorkingEmptyState';
import { PreviewEmptyState } from '../Editor/PreviewEmptyState';
import { StepperPreview } from '../StepperPreview';
import { useHtmlStepperEditor } from './HtmlStepperEditorContext';

// Screen height breakpoint for responsive frame sizing
const TALL_SCREEN_BREAKPOINT = 800;

// Device frame configurations
// frameHeight is responsive: uses first value for tall screens (>1000px), second for shorter
const DEVICE_CONFIGS = {
  tablet: {
    frameHeight: { tall: '80vh', short: '90vh' },
    screenHeightPercent: 97,
    screenAspectRatio: '580/760',
    borderRadius: '3vh',
  },
  mobile: {
    frameHeight: { tall: '75vh', short: '90vh' },
    screenHeightPercent: 97.5,
    screenAspectRatio: '393/852',
    borderRadius: '4vh',
  },
} as const;

// Hook to detect if screen is "tall" (height > breakpoint)
function useIsTallScreen() {
  const [isTall, setIsTall] = useState(() =>
    typeof window !== 'undefined'
      ? window.innerHeight > TALL_SCREEN_BREAKPOINT
      : true,
  );

  useEffect(() => {
    const handleResize = () => {
      setIsTall(window.innerHeight > TALL_SCREEN_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return isTall;
}

export const HtmlStepperEditorPreview = () => {
  const { state, data, iframe, previewRef } = useHtmlStepperEditor();
  const { device, editorMode } = state;
  const { formData } = data;
  const { iframeId } = iframe;

  const { getAgentState } = useAgentChat();

  const isAgentWorking = useMemo(() => {
    const agentState = getAgentState();
    return agentState.isLoading || agentState.isWorking;
  }, [getAgentState]);

  const hasContent = useMemo(() => {
    // Has content if there's a wrapper or at least one step with HTML
    const hasWrapper = !!formData?.wrapper;
    const hasSteps = (formData?.steps?.length ?? 0) > 0;
    return hasWrapper || hasSteps;
  }, [formData]);

  const shouldShowEmptyState = formData && !hasContent;

  // Hide preview in logic mode but keep it mounted to preserve iframe state
  const isHidden = editorMode === 'logic';
  const isTallScreen = useIsTallScreen();

  // Get the appropriate frame height based on screen size
  const getFrameHeight = (deviceType: 'tablet' | 'mobile') => {
    const config = DEVICE_CONFIGS[deviceType];
    return isTallScreen ? config.frameHeight.tall : config.frameHeight.short;
  };

  return (
    <div
      className={classNames(
        'relative h-full w-full overflow-hidden bg-white px-2',
        isHidden && 'pointer-events-none invisible',
      )}
    >
      {/* Device frames/wrappers - shown conditionally but WebsitePreview stays mounted */}
      <div className="absolute inset-0 px-2">
        {/* Desktop Frame */}
        <div
          className={`h-full w-full ${device === 'desktop' ? 'block' : 'hidden'}`}
        >
          <div className="h-full w-full overflow-clip rounded-tl-2xl rounded-tr-2xl border-0 bg-white">
            {/* Placeholder for desktop */}
          </div>
        </div>

        {/* Tablet Frame */}
        {device === 'tablet' && (
          <div className="flex h-full w-full items-center justify-center">
            <div className="relative">
              <svg
                className="w-auto"
                style={{ height: getFrameHeight('tablet') }}
                width="592"
                height="772"
                viewBox="0 0 592 772"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3.32812 37.8658C3.32812 17.8538 19.5511 1.63086 39.5631 1.63086H553.517C573.529 1.63086 589.752 17.8538 589.752 37.8659V730.834C589.752 750.846 573.529 767.069 553.517 767.069H39.5631C19.5511 767.069 3.32812 750.846 3.32812 730.834V37.8658Z"
                  fill="#0B0921"
                />
                <path
                  d="M589.751 87.8818C590.755 87.8821 591.569 88.6968 591.569 89.7012V112.983C591.569 113.988 590.755 114.802 589.751 114.802V87.8818ZM589.751 55.1416C590.755 55.1418 591.569 55.9557 591.569 56.96V80.2422C591.569 81.2466 590.755 82.0613 589.751 82.0615V55.1416ZM550.831 0.211914C551.835 0.211931 552.65 1.02595 552.65 2.03027H520.273C520.274 1.02611 521.088 0.212176 522.092 0.211914H550.831Z"
                  fill="#0B0921"
                />
                <path
                  d="M6.59375 40.2161C6.59375 20.6044 22.4921 4.70605 42.1037 4.70605H551.345C570.956 4.70605 586.855 20.6044 586.855 40.2161V728.661C586.855 748.272 570.956 764.171 551.345 764.171H42.1037C22.4921 764.171 6.59375 748.272 6.59375 728.661V40.2161Z"
                  fill="#36363E"
                />
              </svg>
            </div>
          </div>
        )}

        {/* Mobile Frame - iPhone 15/16 (393x852) */}
        {device === 'mobile' && (
          <div className="flex h-full w-full items-center justify-center">
            <div className="relative">
              <svg
                className="w-auto"
                style={{ height: getFrameHeight('mobile') }}
                width="417"
                height="876"
                viewBox="0 0 417 876"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                {/* Outer frame */}
                <rect
                  x="3"
                  y="0"
                  width="411"
                  height="876"
                  rx="55"
                  fill="#0B0921"
                />
                {/* Inner screen area */}
                <rect
                  x="9"
                  y="6"
                  width="399"
                  height="864"
                  rx="50"
                  fill="#36363E"
                />
                {/* Left side buttons */}
                <rect
                  x="0"
                  y="180"
                  width="3"
                  height="55"
                  rx="1.5"
                  fill="#0B0921"
                />
                <rect
                  x="0"
                  y="250"
                  width="3"
                  height="55"
                  rx="1.5"
                  fill="#0B0921"
                />
                <rect
                  x="0"
                  y="130"
                  width="3"
                  height="30"
                  rx="1.5"
                  fill="#0B0921"
                />
                {/* Right side button */}
                <rect
                  x="414"
                  y="220"
                  width="3"
                  height="90"
                  rx="1.5"
                  fill="#0B0921"
                />
              </svg>
            </div>
          </div>
        )}
      </div>

      {/* WebsitePreview - Always mounted, positioned absolutely */}
      <div className="pointer-events-none relative h-full w-full">
        <div
          className={classNames(
            'pointer-events-auto overflow-clip transition-all duration-100',
            device === 'desktop' &&
              'absolute top-0 left-0 h-full w-full rounded-tl-2xl rounded-tr-2xl bg-white',
            device !== 'desktop' && 'absolute top-1/2 left-1/2 w-auto',
          )}
          style={
            device === 'desktop'
              ? {}
              : {
                  height: `calc(${getFrameHeight(device)} * ${DEVICE_CONFIGS[device].screenHeightPercent} / 100)`,
                  aspectRatio: DEVICE_CONFIGS[device].screenAspectRatio,
                  borderRadius: DEVICE_CONFIGS[device].borderRadius,
                  transform: 'translate(-50%, -50%)',
                }
          }
        >
          <div className="relative h-full w-full">
            <StepperPreview
              id={iframeId}
              iframeRef={previewRef}
              device={device}
              title="HTML Stepper Preview"
            />

            {shouldShowEmptyState && (
              <div className="absolute inset-0 flex items-center justify-center bg-white">
                {isAgentWorking ? (
                  <AgentWorkingEmptyState />
                ) : (
                  <PreviewEmptyState />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
